import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorIcon from '@mui/icons-material/Error';
import BuildIcon from '@mui/icons-material/Build';
import WarningIcon from '@mui/icons-material/Warning';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import VisibilityIcon from '@mui/icons-material/Visibility';
import propertyService from '../../services/propertyService';
import AddIcon from '@mui/icons-material/Add';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import { InspectionEvaluationResponse, InspectionEvaluationIssue } from '../../models/inspectionEvaluationIssue';
import { ImageInspectionRegistry } from '../../models/property';
import PropertyAddInspectionImageUpload from './PropertyAddInspectionImageUpload';
import RoomImagesAccordion from './RoomImagesAccordion';

interface PropertyAddInspectionPicturesProps {
  propertyId: string;
  inspectionEvaluation: InspectionEvaluationResponse | null;
  inspectionId: string | null;
}

const PropertyAddInspectionPictures: React.FC<PropertyAddInspectionPicturesProps> = ({
  propertyId,
  inspectionEvaluation,
  inspectionId
}) => {
  const [registry, setRegistry] = useState<ImageInspectionRegistry | null>(null);
  const [carouselImages, setCarouselImages] = useState<{ url: string; description: string; redFlags: string[]; size:string; }[]>([]);
  const [isCarouselOpen, setIsCarouselOpen] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportMessage, setReportMessage] = useState<string | null>(null);
  const [isImagesSectionVisible, setIsImagesSectionVisible] = useState<boolean>(false);


  useEffect(() => {
    const fetchRegistry = async () => {
      try {
        const initialRegistry = await propertyService.getPropertyInspectionRegistry(propertyId);
        setRegistry(initialRegistry);
      } catch (error) {
        console.error('Error fetching inspection registry:', error);
      }
    };

    fetchRegistry();
  }, [propertyId]);

  const fetchImagesForIssue = async (context: string[]): Promise<{ url: string; description: string; redFlags: string[], size: string }[]> => {
    if (!registry) return [];
    const images = registry.data.filter((item) => context.includes(item.imageId || ''));
    const results = await Promise.all(
      images.map(async (image) => ({
        url: image.imageId ? await propertyService.getPropertyImageUrl(propertyId, image.imageId) : '',
        description: image.Description,
        redFlags: image.RedFlags,
        size: image.EstimatedSize
      }))
    );
    return results;
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Safety':
        return <WarningIcon color="error" sx={{ marginRight: 1 }} />;
      case 'Repair':
        return <BuildIcon color="primary" sx={{ marginRight: 1 }} />;
      case 'Improvement':
        return <UpgradeIcon color="success" sx={{ marginRight: 1 }} />;
      case 'Monitor':
        return <VisibilityIcon color="action" sx={{ marginRight: 1 }} />;
      case 'Cleanliness':
        return <CleaningServicesIcon color="info" sx={{ marginRight: 1 }} />;
      default:
        return <ErrorIcon color="warning" sx={{ marginRight: 1 }} />;
    }
  };

  const handleGenerateReport = async () => {
    setIsGeneratingReport(true);
    setReportMessage(null);

    try {
      await propertyService.createInspectionReportFromImageRegistry(propertyId);
      setReportMessage('Inspection report generated successfully.');
    } catch (error) {
      console.error('Error generating inspection report:', error);
      setReportMessage('Failed to generate inspection report. Please try again.');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleCreateWorkItem = async () => {
    try {
      if(inspectionId){
        await propertyService.createWorkItemFromInspectionRecord(propertyId, inspectionId);
      }
    } catch (error) {
      console.error('Error creating work item:', error);
    }
  };

  const handleOpenCarousel = async (context: string[]) => {
    const imagesWithDetails = await fetchImagesForIssue(context);
    setCarouselImages(imagesWithDetails);
    setIsCarouselOpen(true);
  };

  const InspectionEvaluationIssueDisplay = ({
    issue,
  }: {
    issue: InspectionEvaluationIssue;
  }) => {
    return (
      <Card sx={{ mb: 2, boxShadow: 'none', border: 'none', borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {getCategoryIcon(issue.category)}
            <Typography variant="h6" sx={{ mr: 2 }}>
              {issue.shortDescription}
            </Typography>
            <Chip
              label={`Severity: ${issue.severity}`}
              color={issue.severity > 7 ? 'error' : issue.severity > 4 ? 'warning' : 'default'}
              size="small"
            />
          </Box>
          <Typography variant="body2">
            <strong>Location:</strong> {issue.location}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {issue.longDescription}
          </Typography>
          {issue.context?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {issue.context?.length} image(s) available
              </Typography>
              <Button variant="outlined" size="small" onClick={() => handleOpenCarousel(issue.context)}>
                View Images
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const issuesBySeverity = inspectionEvaluation?.issues.reduce(
    (acc, issue) => {
      if (issue.severity <= 3) {
        acc.observations.push(issue);
      } else {
        acc.main.push(issue);
      }
      return acc;
    },
    { main: [] as InspectionEvaluationIssue[], observations: [] as InspectionEvaluationIssue[] }
  );

  return (
    <>
      {inspectionEvaluation && (
        <>
                  { inspectionId &&
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={() => handleCreateWorkItem()}
            >
              Create Work Item
            </Button>
          </Box>
          }
          <Typography variant="h5" sx={{ mt: 4 }}>
            Inspection Issues
          </Typography>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Issues ({inspectionEvaluation?.issues?.length || 0})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {inspectionEvaluation?.issues.map((issue) => (
                <InspectionEvaluationIssueDisplay key={issue.id} issue={issue} />
              ))}
            </AccordionDetails>
          </Accordion>
          {inspectionEvaluation.recommendedActions && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Recommended Actions</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {inspectionEvaluation.recommendedActions.map((action, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={action} />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
        </>
      )}
      
      {registry && (
  <Box sx={{ mt: 4 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
      <Typography variant="h6">Images</Typography>
      <IconButton
        onClick={() => setIsImagesSectionVisible(!isImagesSectionVisible)}
        aria-label={isImagesSectionVisible ? 'Hide Images' : 'Show Images'}
      >
        {isImagesSectionVisible ? <RemoveCircleOutlineIcon /> : <AddCircleOutlineIcon />}
      </IconButton>
    </Box>
    {isImagesSectionVisible && (
      <RoomImagesAccordion registry={registry} propertyId={propertyId} />
    )}
  </Box>
)}


      <PropertyAddInspectionImageUpload
        propertyId={propertyId}
        registry={registry}
        onRegistryUpdate={async () => {
          try {
            const updatedRegistry = await propertyService.getPropertyInspectionRegistry(propertyId);
            setRegistry(updatedRegistry);
          } catch (error) {
            console.error('Error refreshing registry:', error);
          }
        }}
      />
      <Box sx={{ mt: 4 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerateReport}
          disabled={isGeneratingReport}
          fullWidth
        >
          {isGeneratingReport ? 'Generating Report...' : 'Generate Inspection Report'}
        </Button>
        {reportMessage && (
          <Typography
            variant="body2"
            sx={{ mt: 2, color: reportMessage.includes('successfully') ? 'green' : 'red' }}
          >
            {reportMessage}
          </Typography>
        )}
      </Box>
      <Dialog open={isCarouselOpen} onClose={() => setIsCarouselOpen(false)} fullWidth maxWidth="md">
        <DialogContent>
          {carouselImages.map((image, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Box
                component="img"
                src={image.url}
                alt={`Carousel Image ${index + 1}`}
                sx={{ width: '100%' }}
              />
{/*               <Typography variant="body2" sx={{ mt: 1 }}>
                {image.description}
              </Typography>
              {image.redFlags.length > 0 && (
                <List>
                  {image.redFlags.map((flag, idx) => (
                    <ListItem key={idx} disablePadding>
                      <Typography variant="body2" color="error">
                        - {flag}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              )}
              {
                image.size && (
                  <>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                      {image.size}
                  </Typography>
                  </>
                )
              } */}
            </Box>
          ))}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PropertyAddInspectionPictures;